'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
  type FirestoreError,
  type QuerySnapshot,
} from 'firebase/firestore';
import { ArrowRight, Rss } from 'lucide-react';

import { db } from '@/lib/firebase/config';
import type { BlogPost } from '@/types/admin';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { HomeHeader } from '@/components/home/home-header';
import { Skeleton } from '@/components/ui/skeleton';

function formatDate(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString();
}

export default function BlogIndexPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const handleSnapshot = useCallback((snap: QuerySnapshot<DocumentData>) => {
    setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as BlogPost[]);
    setLoading(false);
  }, []);

  const handleSnapshotError = useCallback((error: FirestoreError) => {
    console.error('Error loading blog posts:', error);
    if (error.code === 'failed-precondition') {
      setLoadError('Firestore index required. Deploy indexes then refresh.');
    } else {
      setLoadError('Failed to load blog posts. Check console for details.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const q = query(
      collection(db, 'blog-posts'),
      where('published', '==', true),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsub = onSnapshot(q, handleSnapshot, handleSnapshotError);

    return () => unsub();
  }, [handleSnapshot, handleSnapshotError]);

  return (
    <div className="min-h-screen">
      <HomeHeader />

      <main className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-3">
              <Rss className="h-5 w-5 text-primary" />
              <h1 className="text-4xl font-bold tracking-tight">Blog</h1>
            </div>
            <p className="text-muted-foreground text-lg">
              Practical guides and updates to help you master Prompt Engineering, AI Agents, and Vibe Coding.
            </p>
          </div>

          <div className="mt-10">
            {loadError ? (
              <Card className="border-2">
                <CardHeader>
                  <CardTitle>Blog not loading</CardTitle>
                  <CardDescription>{loadError}</CardDescription>
                </CardHeader>
              </Card>
            ) : loading ? (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <Card key={idx} className="border-2">
                    <CardHeader className="space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                    </CardHeader>
                    <CardContent>
                      <Skeleton className="h-4 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : posts.length === 0 ? (
              <Card className="border-2">
                <CardHeader>
                  <CardTitle>No posts yet</CardTitle>
                  <CardDescription>Publish a post in the admin panel to show it here.</CardDescription>
                </CardHeader>
              </Card>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {posts.map((post) => (
                  <Card key={post.id} className="border-2 hover:border-primary/50 transition-all hover:shadow-lg">
                    {post.coverImage ? (
                      <div className="relative w-full h-44">
                        <Image
                          src={post.coverImage}
                          alt={post.title}
                          fill
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          className="object-cover rounded-t-lg"
                        />
                      </div>
                    ) : null}
                    <CardHeader className="space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <Badge variant="secondary">{post.category}</Badge>
                        <span className="text-xs text-muted-foreground">{post.readTime} min</span>
                      </div>
                      <CardTitle className="text-xl line-clamp-2">{post.title}</CardTitle>
                      <CardDescription className="line-clamp-3">{post.excerpt}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex items-center justify-between gap-4">
                      <span className="text-xs text-muted-foreground">{formatDate(post.publishedAt || post.createdAt)}</span>
                      <Link href={`/blog/${post.slug}`} className="ml-auto">
                        <Button variant="outline" size="sm">
                          Read
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
